/**
 * Target acquisition (TRD-05 §3).
 *
 * Two rules carry the weight here.
 *
 * Every comparator is TOTAL (R-05-05). Ties break on the lowest entity id, always, with
 * no exceptions and no "it can't happen in practice". Two enemies spawned in the same
 * wave have identical hp and identical pathT for as long as nothing has hit them, so ties
 * are the common case rather than the corner case, and a comparator that leaves them
 * unordered makes the winner depend on array order — which survives one machine and
 * fails on another, months later, as a rejected leaderboard submission.
 *
 * Targets PERSIST (R-05-06). A tower keeps its target while that target is alive and in
 * range, and only re-acquires when it dies or leaves. Cheaper, and it removes the visible
 * jitter of a turret that snaps between two equidistant enemies every tick.
 */
import type { Enemy, EntityPool, Tower } from '../entities.js';
import { distSq } from '../math.js';
import { NO_TARGET, type EntityId, type TargetingMode } from '../types.js';
import type { SpatialGrid } from './spatial.js';

/**
 * A tower's own range at its current star, BEFORE any synergy mod (playtest request).
 *
 * Separate from `effectiveRange` on purpose, and this is the distinction that matters:
 * an aura whose radius follows the carrier's range has to use THIS one. Feeding it the
 * effective range would let an aura's reach depend on range granted by another aura, and
 * then the answer depends on which order the two were evaluated in — circular reach is
 * not a debugging session anyone needs. Stars are authored data and cannot form a cycle.
 */
export function starRange(tower: Tower): number {
  return tower.range * (tower.rangeScaling[tower.star - 1] ?? 1);
}

/** Effective range in tiles, after synergy mods (TRD-08 writes them, we only read). */
export function effectiveRange(tower: Tower): number {
  return (starRange(tower) + tower.mods.rangeBonus) * tower.mods.rangeMultiplier;
}

export function isInRange(tower: Tower, enemy: Enemy): boolean {
  const range = effectiveRange(tower);
  return distSq(tower.x, tower.y, enemy.x, enemy.y) <= range * range;
}

/**
 * Can this tower SEE that point, or is there terrain in the way (playtest request)?
 *
 * Blockers used to be pure decoration for the build mask: they denied you a tile to stand
 * on and then let you shoot straight through themselves, which reads as an oversight the
 * moment anyone notices it. Making them opaque is also the cheapest push toward spreading
 * a board out — a clump behind a rock covers far less than it looks like it does.
 *
 * A supercover tile walk, in integers only. No trig and no `Math.pow`, because this runs
 * inside the sim and the determinism contract forbids both (R-00-04); floats appear only
 * as the two endpoint coordinates, and every branch below is a comparison rather than an
 * accumulation, so client and server agree bit for bit.
 *
 * The endpoint tiles are deliberately NOT checked. An enemy standing on a blocker is
 * unreachable anyway, and a tower is never on one — but a shooter whose own tile the mask
 * happens to call solid would otherwise be blind for the whole run.
 */
export function hasLineOfSight(
  blocked: (tileX: number, tileY: number) => boolean,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): boolean {
  let tx = Math.floor(x0);
  let ty = Math.floor(y0);
  let endX = Math.floor(x1);
  let endY = Math.floor(y1);
  if (tx === endX && ty === endY) return true;

  // Walk from the lexicographically smaller tile, always.
  //
  // Bresenham is NOT symmetric: where the line clips a corner exactly, A-to-B and
  // B-to-A step through different tiles, so two towers either side of the same wall
  // could disagree about whether the gap is open. Normalising the direction makes sight
  // a property of the PAIR, which is both correct and what a player expects.
  if (tx > endX || (tx === endX && ty > endY)) {
    const swapX = tx;
    const swapY = ty;
    tx = endX;
    ty = endY;
    endX = swapX;
    endY = swapY;
  }

  const dx = Math.abs(endX - tx);
  const dy = Math.abs(endY - ty);
  const stepX = endX > tx ? 1 : -1;
  const stepY = endY > ty ? 1 : -1;

  // Bresenham with the error term doubled, so the whole walk stays in integers.
  let err = dx - dy;
  // The walk is bounded by the tiles it can cross; the guard is belt and braces against
  // a NaN coordinate turning a sight check into an infinite loop.
  for (let guard = 0; guard < dx + dy + 2; guard++) {
    if (tx === endX && ty === endY) return true;
    const e2 = err * 2;
    if (e2 > -dy) {
      err -= dy;
      tx += stepX;
    } else if (e2 < dx) {
      err += dx;
      ty += stepY;
    }
    if (tx === endX && ty === endY) return true;
    if (blocked(tx, ty)) return false;
  }
  return true;
}

/**
 * Resolve this tick's target for one tower.
 *
 * Returns the existing target unchanged when it is still valid, so the common path costs
 * one distance check rather than a range query.
 */
export function acquireTarget(
  tower: Tower,
  enemies: EntityPool<Enemy>,
  grid: SpatialGrid,
  scratch: EntityId[],
  /** Terrain opacity. Omitted, nothing blocks — which is what a bare test world wants. */
  blocked?: (tileX: number, tileY: number) => boolean,
): EntityId {
  const visible = (enemy: Enemy): boolean =>
    blocked === undefined || hasLineOfSight(blocked, tower.x, tower.y, enemy.x, enemy.y);

  if (tower.targetId !== NO_TARGET) {
    const current = enemies.get(tower.targetId);
    // Losing sight drops the target, so a tower does not keep firing at something that
    // has walked behind a rock (R-05-06 persistence stops at the wall).
    if (current && isInRange(tower, current) && visible(current)) return tower.targetId;
  }

  grid.query(tower.x, tower.y, effectiveRange(tower), enemies, scratch);
  if (scratch.length === 0) return NO_TARGET;

  let best: Enemy | undefined;
  for (let i = 0; i < scratch.length; i++) {
    const candidate = enemies.get(scratch[i] as EntityId);
    if (!candidate || !visible(candidate)) continue;
    if (!best || beats(candidate, best, tower)) best = candidate;
  }
  if (!best) return NO_TARGET;
  return best.id;
}

/**
 * Is `a` a better target than `b` for this tower?
 *
 * Written as a strict "beats" predicate rather than a sort comparator because a sort
 * would cost O(n log n) and an allocation per tower per tick to answer a question that is
 * a single linear scan. The tie-break is the final clause of every branch, never omitted.
 */
function beats(a: Enemy, b: Enemy, tower: Tower): boolean {
  switch (tower.targetingMode) {
    case 'first':
      return a.pathT !== b.pathT ? a.pathT > b.pathT : a.id < b.id;
    case 'last':
      return a.pathT !== b.pathT ? a.pathT < b.pathT : a.id < b.id;
    case 'closest': {
      const da = distSq(tower.x, tower.y, a.x, a.y);
      const db = distSq(tower.x, tower.y, b.x, b.y);
      return da !== db ? da < db : a.id < b.id;
    }
    case 'strongest':
      return a.hp !== b.hp ? a.hp > b.hp : a.id < b.id;
    case 'weakest':
      return a.hp !== b.hp ? a.hp < b.hp : a.id < b.id;
  }
}

export const TARGETING_MODES: readonly TargetingMode[] = [
  'first',
  'last',
  'closest',
  'strongest',
  'weakest',
];

export function isTargetingMode(value: string): value is TargetingMode {
  return (TARGETING_MODES as readonly string[]).includes(value);
}
